<template>
  <aside
    v-if="authStore.isAuthenticated"
    class="account-rail"
    :class="{ 'account-rail--standalone': standalone }"
    data-testid="app-account-rail"
  >
    <div class="account-rail-user">
      <v-icon icon="mdi-account-circle" size="20" />
      <span class="account-rail-email" :title="authStore.user?.email">
        {{ authStore.user?.email }}
      </span>
    </div>

    <button
      type="button"
      class="account-rail-link account-rail-link--credits"
      data-testid="account-rail-credits"
      @click="onCreditsClick"
    >
      <v-icon icon="mdi-credit-card-plus" size="18" />
      <span class="account-rail-link-text">
        <span class="account-rail-link-title">
          {{ t('nav.menu.creditsBalance', { count: authStore.credits || 0 }) }}
        </span>
        <span class="account-rail-link-sub">{{ t('analysis.buyCredits') }}</span>
      </span>
    </button>

    <router-link
      class="account-rail-link"
      :to="{ name: 'AnalysesDashboard' }"
      :class="{ 'account-rail-link--active': activeAnalysesDashboard }"
      data-testid="account-rail-analyses"
      @click="onAnalysesClick"
    >
      <v-icon icon="mdi-folder-multiple-image" size="18" />
      <span>{{ t('nav.menu.analysis') }}</span>
    </router-link>

    <div class="account-rail-divider" aria-hidden="true" />

    <button
      type="button"
      class="account-rail-link account-rail-link--signout"
      data-testid="account-rail-signout"
      @click="onSignOutClick"
    >
      <v-icon icon="mdi-logout" size="18" />
      <span>{{ t('nav.auth.signOut') }}</span>
    </button>

    <!-- Buying credits opens the checkout directly here instead of routing a
         signed-in user back out to the marketing landing's pricing section. -->
    <PayPalButton
      v-model="showCreditCheckout"
      source="account_rail"
      context="default"
    />
  </aside>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { auth } from '@/firebase/config';
import { useAuthStore } from '@/stores/auth';
import { trackPricingClick } from '@/utils/analytics';
import PayPalButton from '@/components/common/PayPalButton.vue';

defineProps({
  // True when the rail is the only rail on the page (i.e. not nested
  // inside another sidebar like AnalysisSideMenu). Used to switch
  // between the embedded-section look and the standalone-card look.
  standalone: { type: Boolean, default: false },
});

const router = useRouter();
const store = useStore();
const { t } = useI18n();
const authStore = useAuthStore();

const activeAnalysesDashboard = computed(
  () => router.currentRoute.value.name === 'AnalysesDashboard',
);

const showCreditCheckout = ref(false);

function onCreditsClick() {
  trackPricingClick('account_rail');
  // A signed-in user already has an account + credit context, so open the
  // checkout modal in place instead of routing back out to the marketing
  // landing's pricing section (which made them scroll past the full hero).
  showCreditCheckout.value = true;
}

function onAnalysesClick() {
  // Drop any in-memory analysis state when jumping to the history
  // dashboard so the user doesn't see stale data when they return to
  // an analysis-scoped surface.
  store.commit('resetAnalysisData');
}

async function onSignOutClick() {
  await auth.signOut();
  store.commit('resetState');
  router.push('/');
}
</script>

<style scoped>
.account-rail {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.account-rail--standalone {
  padding: 12px;
  border: 1px solid rgba(41, 70, 86, 0.14);
  border-radius: 8px;
  background: #ffffff;
}

.account-rail-user {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 8px 10px 10px;
  color: #2d4350;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.2;
}

.account-rail-email {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-rail-divider {
  height: 1px;
  margin: 6px 4px;
  background: rgba(41, 70, 86, 0.12);
}

.account-rail-link {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 38px;
  padding: 8px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #263f48;
  font: inherit;
  font-size: 0.88rem;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.account-rail-link:hover,
.account-rail-link:focus-visible {
  background: rgba(36, 83, 70, 0.08);
}

.account-rail-link--active {
  background: #edf4f1;
  color: #245346;
  font-weight: 700;
  box-shadow: inset 3px 0 0 #245346;
}

.account-rail-link-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.account-rail-link-title {
  font-weight: 700;
  color: #172b33;
}

.account-rail-link-sub {
  color: #55717c;
  font-size: 0.75rem;
}

.account-rail-link--credits {
  background: rgba(245, 234, 217, 0.55);
  color: #5d4423;
}

.account-rail-link--credits:hover,
.account-rail-link--credits:focus-visible {
  background: rgba(245, 234, 217, 0.85);
}

.account-rail-link--signout {
  color: #8c3a2e;
}

.account-rail-link--signout:hover,
.account-rail-link--signout:focus-visible {
  background: rgba(140, 58, 46, 0.08);
}
</style>
